import { useEffect, useMemo, useRef } from 'react';

import {
  ChartRequestSH,
  ChartResponseSH,
  EChartsOption,
  WindxSimulacaoAntiga,
  WindxTela,
} from 'helper/windx';
import { useLazyGetChartDataSHQuery } from 'services/api/windx';

interface UseChartDataMergedSHParams {
  /**
   * Payload base SEM data_simulacao (Estudo Automatico). undefined enquanto
   * filtros incompletos do Estudo Automatico (subdiv + ent + modelos + RMV +
   * tipoRodada). Em FE-WINDX-043 v7 PODE ser undefined sem bloquear o
   * grafico — se houver pelo menos 1 sim antiga ativa, ela vira a base.
   */
  payloadBase: ChartRequestSH | undefined;
  /** Tela ativa: hook so dispara quando telaAtiva === 'sh'. */
  telaAtiva: WindxTela;
  /** Counter do botao Realizar Consulta. */
  triggerConsulta: number;
  /**
   * Simulacoes antigas ATIVAS (multi-checkbox, max 3). Cada uma gera uma
   * chamada paralela ao /chart/data/ com filtros proprios (id_modelos,
   * tipo_rodada, opcoes_rmv, data_simulacao=hoje-dias_atras).
   */
  simsAtivas: WindxSimulacaoAntiga[];
  /**
   * Filtros minimos compartilhados (subdiv, entidades, periodo). Usados
   * para construir o payload das simulacoes antigas quando o Estudo
   * Automatico nao esta configurado.
   */
  filtrosCompartilhados: Pick<
  ChartRequestSH,
  'frequencia_temporal' | 'subdivisao_espacial' | 'id_entidades'
  | 'data_inicio' | 'data_fim' | 'unidade'
  > | undefined;
}

interface UseChartDataMergedSHResult {
  /** ChartResponseSH com series MESCLADAS de Estudo Automatico + N Sim Antigas. */
  data: ChartResponseSH | undefined;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Resolve `dias_atras` para data DD/MM em hora local. Usado para sufixar
 * a legenda das simulacoes antigas com a Data de Previsao em vez do nome
 * dado pelo usuario (que pode ser longo e poluir a legenda).
 *
 * Exemplo: hoje=2026-05-26 + dias_atras=1 -> "25/05"
 */
function formatarDataPrevisao(diasAtras: number): string {
  const hoje = new Date();
  const target = new Date(hoje);
  target.setDate(hoje.getDate() - diasAtras);
  const dd = String(target.getDate()).padStart(2, '0');
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

/**
 * Renomeia/decora series de uma simulacao antiga: aplica `lineStyle.type='dashed'`
 * + opacity 0.85 + sufixa o nome com `[Dt Prev DD/MM]` para o usuario
 * identificar a qual rodada cada curva pertence.
 *
 * FE-WINDX-043 v8 (smoke turno 8 PO 2026-05-26): sufixo trocado de
 * `[${sim.nome}]` para `[Dt Prev DD/MM]`. Motivacao: nome do usuario pode
 * chegar a 30 chars e poluir a legenda quando ha multiplas sims ativas.
 * "Dt Prev" + data eh universal, conciso, e ainda permite diferenciar sims
 * por data (que eh o atributo mais relevante para o analista).
 */
function decorarSeriesSimAntiga(
  optionAntiga: EChartsOption,
  sim: WindxSimulacaoAntiga,
): Record<string, unknown>[] {
  const seriesAntiga = Array.isArray(optionAntiga.series)
    ? (optionAntiga.series as Record<string, unknown>[])
    : [];
  const sufixo = ` [Dt Prev ${formatarDataPrevisao(sim.dias_atras)}]`;
  return seriesAntiga.map((s) => {
    const lineStyleAtual = (s.lineStyle as Record<string, unknown>) ?? {};
    return {
      ...s,
      name: `${s.name as string}${sufixo}`,
      lineStyle: { ...lineStyleAtual, type: 'dashed', opacity: 0.85 },
    };
  });
}

/**
 * Constroi payload para chamar /chart/data/ no contexto de uma simulacao
 * antiga: combina os filtros compartilhados (subdiv, ent, periodo) com os
 * filtros SALVOS da sim (id_modelos, tipo_rodada, opcoes_rmv) + a data
 * resolvida em runtime (hoje - dias_atras).
 */
function buildPayloadSimAntiga(
  shared: NonNullable<UseChartDataMergedSHParams['filtrosCompartilhados']>,
  sim: WindxSimulacaoAntiga,
): ChartRequestSH {
  const hoje = new Date();
  const target = new Date(hoje);
  target.setDate(hoje.getDate() - sim.dias_atras);
  const yyyy = target.getFullYear();
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  const dd = String(target.getDate()).padStart(2, '0');
  return {
    ...shared,
    id_modelos: sim.id_modelos,
    tipo_rodada: sim.tipo_rodada,
    opcoes_rmv: sim.opcoes_rmv,
    data_simulacao: `${yyyy}-${mm}-${dd}`,
  };
}

/**
 * Hook orquestrador FE-WINDX-043 v7: 1+N chamadas paralelas a /chart/data/
 * com merge transparente das series no `option` ECharts.
 *
 * - Estudo Automatico: 1 chamada (se `payloadBase` definido)
 * - Simulacoes Antigas: ate 3 chamadas (1 por sim ativa em simsAtivas)
 * - Merge: series do automatico + series decoradas das sims (dashed +
 *   sufixo [nome] para cada uma)
 *
 * Falha parcial degrada graciosa: se a sim X falha mas o automatico OK,
 * grafico mostra apenas o automatico (eventual sim que chegou) sem
 * bloquear. Falha total do automatico SEM sim ativa = isError=true.
 *
 * Limite 3 sims hardcoded aqui para casar com LIMITE_SIMULACOES_ANTIGAS_ATIVAS
 * do reducer — 3 hooks `useLazyGetChartDataSHQuery` fixos (React nao permite
 * quantidade variavel de hooks).
 */
export default function useChartDataMergedSH(
  params: UseChartDataMergedSHParams,
): UseChartDataMergedSHResult {
  const {
    payloadBase, telaAtiva, triggerConsulta, simsAtivas, filtrosCompartilhados,
  } = params;

  const [getChartAuto, autoResult] = useLazyGetChartDataSHQuery();
  const [getChartSim0, sim0Result] = useLazyGetChartDataSHQuery();
  const [getChartSim1, sim1Result] = useLazyGetChartDataSHQuery();
  const [getChartSim2, sim2Result] = useLazyGetChartDataSHQuery();

  const payloadsSim = useMemo<(ChartRequestSH | undefined)[]>(() => {
    if (!filtrosCompartilhados) return [undefined, undefined, undefined];
    const builds: (ChartRequestSH | undefined)[] = [undefined, undefined, undefined];
    simsAtivas.slice(0, 3).forEach((sim, i) => {
      builds[i] = buildPayloadSimAntiga(filtrosCompartilhados, sim);
    });
    return builds;
  }, [filtrosCompartilhados, simsAtivas]);

  const lastTrigger = useRef(0);
  // Granularidade da ultima consulta disparada. Quando o trigger vem da troca
  // Semi-horario <-> Diario (setGranularidadeSH), so a `frequencia_temporal`
  // mudou: usamos o cache do RTK Query (preferCacheValue) para que voltar a
  // uma sub-aba ja consultada reexiba o resultado sem novo request. Cliques
  // em Realizar Consulta com a mesma granularidade continuam buscando do BE.
  const lastFrequencia = useRef<ChartRequestSH['frequencia_temporal'] | undefined>(undefined);

  useEffect(() => {
    // Defesa em profundidade (WINDX-bug-limpar-filtros, 2026-05-26): se o
    // counter regrediu (resetado para 0 por algum reducer), re-sincronizamos
    // o ref para nao bloquear o proximo realizarConsulta(). O fix primario
    // eh no reducer (preserva triggerConsulta), este gate protege contra
    // futuras regressoes onde alguem volte a zerar o counter.
    if (triggerConsulta === 0) {
      lastTrigger.current = 0;
      return;
    }
    if (telaAtiva !== 'sh') return;
    if (triggerConsulta <= lastTrigger.current) return;
    // Pelo menos uma fonte deve estar configurada (auto OU alguma sim)
    const temAuto = payloadBase !== undefined;
    const temAlgumaSim = payloadsSim.some((p) => p !== undefined);
    if (!temAuto && !temAlgumaSim) return;
    lastTrigger.current = triggerConsulta;
    const frequencia = filtrosCompartilhados?.frequencia_temporal;
    const preferCache = frequencia !== lastFrequencia.current;
    lastFrequencia.current = frequencia;
    if (payloadBase) getChartAuto(payloadBase, preferCache);
    if (payloadsSim[0]) getChartSim0(payloadsSim[0], preferCache);
    if (payloadsSim[1]) getChartSim1(payloadsSim[1], preferCache);
    if (payloadsSim[2]) getChartSim2(payloadsSim[2], preferCache);
  }, [
    telaAtiva, triggerConsulta, payloadBase, payloadsSim, filtrosCompartilhados,
    getChartAuto, getChartSim0, getChartSim1, getChartSim2,
  ]);

  // Merge das series: base eh o option do automatico (ou do primeiro sim
  // que retornou, se nao ha automatico ativo). Cada sim ativa decora e
  // concatena suas series + adiciona seu legend.data.
  const data = useMemo<ChartResponseSH | undefined>(() => {
    const simResults = [sim0Result, sim1Result, sim2Result];
    let seriesAutomatico: Record<string, unknown>[] = [];
    if (autoResult.data && Array.isArray(autoResult.data.option.series)) {
      seriesAutomatico = autoResult.data.option.series as Record<string, unknown>[];
    }
    const legendAutoData = autoResult.data
      ? (
        ((autoResult.data.option.legend as Record<string, unknown> | undefined)
          ?.data as string[] | undefined
        ) ?? []
      )
      : [];

    let totalPontos = autoResult.data?.metadata.total_pontos ?? 0;
    let totalSeries = autoResult.data?.metadata.total_series ?? 0;
    const seriesAntigas: Record<string, unknown>[] = [];
    const legendAntigas: string[] = [];

    simsAtivas.slice(0, 3).forEach((sim, i) => {
      const r = simResults[i];
      if (!r?.data) return;
      const decoradas = decorarSeriesSimAntiga(r.data.option, sim);
      seriesAntigas.push(...decoradas);
      legendAntigas.push(...decoradas.map((s) => s.name as string));
      totalPontos += r.data.metadata.total_pontos;
      totalSeries += r.data.metadata.total_series;
    });

    // Se nao ha NENHUMA resposta (auto nem sim) ainda, retorna undefined
    // para o consumidor mostrar loading/empty.
    if (!autoResult.data && seriesAntigas.length === 0) return undefined;

    // Option base: prefere o do auto; se nao tiver, usa o do primeiro sim
    // que retornou para ter title/xAxis/yAxis/tooltip etc montados.
    const optionBase = autoResult.data?.option
      ?? simResults.find((r) => r?.data)?.data?.option
      ?? ({} as EChartsOption);
    const legendBase = (optionBase.legend as Record<string, unknown> | undefined) ?? {};

    const seriesMerged = [...seriesAutomatico, ...seriesAntigas];
    const legendMerged = [...legendAutoData, ...legendAntigas];

    const optionMerged: EChartsOption = {
      ...optionBase,
      legend: { ...legendBase, data: legendMerged },
      series: seriesMerged,
    };

    // Metadata base: do auto se houver, do primeiro sim disponivel caso
    // contrario (qualquer um serve para herdar campos como data_inicio etc).
    const metadataBase = autoResult.data?.metadata
      ?? simResults.find((r) => r?.data)?.data?.metadata;
    if (!metadataBase) return undefined;

    return {
      option: optionMerged,
      metadata: {
        ...metadataBase,
        total_pontos: totalPontos,
        total_series: totalSeries,
      },
    };
  }, [autoResult.data, sim0Result, sim1Result, sim2Result, simsAtivas]);

  // isFetching: qualquer query em curso ainda nao retornou
  const isFetching = (
    autoResult.isFetching
    || sim0Result.isFetching
    || sim1Result.isFetching
    || sim2Result.isFetching
  );

  // isError: SH bloqueia o render apenas quando NAO ha nenhum dado disponivel
  // (nem auto, nem nenhuma sim). Erros parciais sao degradacao graciosa.
  const isError = (
    !autoResult.data
    && !sim0Result.data
    && !sim1Result.data
    && !sim2Result.data
    && (
      autoResult.isError
      || sim0Result.isError
      || sim1Result.isError
      || sim2Result.isError
    )
  );

  return {
    data,
    isFetching,
    isError,
    error: autoResult.error
      ?? sim0Result.error
      ?? sim1Result.error
      ?? sim2Result.error,
  };
}
